import {patchState, WritableStateSource} from '@ngrx/signals';

/**
 * Utility functions for a signal store.
 *
 * @template State - The type of the signal store state.
 * @param signalStore - The writable state source of the signal store.
 * @returns An object containing utility functions for the signal store.
 */
const SignalStoreUtiler = <State extends object>(signalStore: WritableStateSource<State>) => {
	return Object.freeze({
		SignalStore: {
			/**
			 * Patches the state of the signal store.
			 *
			 * @param patches - The patches to apply to the state.
			 * @returns The result of the patch operation.
			 */
			patchState: function (patches: Parameters<typeof patchState>[1]) {
				return patchState(signalStore, patches);
			},
		},
		Object: {
			/**
			 * Assigns patches to an object.
			 *
			 * @param obj - The object to which patches will be assigned.
			 * @param patches - The patches to assign to the object.
			 * @returns The object with the assigned patches.
			 */
			assign: function (obj: object, patches: object) {
				return Object.assign(obj, patches);
			},
		},
	});
};

/**
 * Factory function to create a SignalStoreUtiler.
 *
 * @template State - The type of the signal store state.
 * @param signalStore - The writable state source of the signal store.
 * @returns The SignalStoreUtiler for the given signal store.
 */
export function signalStoreUtilerFactory<State extends object>(signalStore: WritableStateSource<State>) {
	return SignalStoreUtiler(signalStore);
}

/* -------------------------------------------------------------------------------------------------------------- */
export const DEFAULT_SIGNAL_STORE_CONFIG = {providedIn: 'root', protectedState: false} as const;
